import {
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  inject,
  input,
  OnDestroy,
  Optional,
  signal,
} from '@angular/core';
import { SiaI18nService } from '../services/i18n.service';
import { SiaSizeDetectorService } from '../services/size-detector.service';
import { SiaTooltipDirective } from './tooltip.directive';

@Directive({
  selector: '[overflowTooltip]',
  standalone: true,
  host: {
    '[attr.disable]': 'tooltipDisabled()',
  },
})
export class OverflowTooltipDirective implements AfterViewInit, OnDestroy {
  // 1. Migrate Inputs to Signal Inputs
  tooltipText = input<string | undefined>(undefined, {
    alias: 'overflowTooltip',
  });
  canBeTranslated = input<boolean>(false);

  // 2. Migrate State (bound to DOM via host binding)
  tooltipDisabled = signal(true);

  private observer!: IntersectionObserver;
  private i18nService = inject(SiaI18nService);
  private sizeDetectorService = inject(SiaSizeDetectorService);

  constructor(
    private el: ElementRef<HTMLElement>,
    @Host() @Optional() private tooltip: SiaTooltipDirective,
  ) {}

  ngAfterViewInit() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        const el = this.el.nativeElement;
        const isClipped = entry.intersectionRatio < 1;
        // Also detect truncation caused by -webkit-line-clamp (rowsNumber):
        // in that case the element stays fully visible in the parent but
        // scrollHeight > clientHeight because text overflows inside the span.
        const isTextOverflow =
          el.scrollHeight > el.clientHeight ||
          el.scrollWidth > el.clientWidth;
        const shouldShowTooltip = isClipped || isTextOverflow;
        this.tooltipDisabled.set(
          !shouldShowTooltip ||
            this.sizeDetectorService.currentSize.isMobile ||
            this.sizeDetectorService.currentSize.deviceType !== 'DESKTOP',
        );
        if (shouldShowTooltip && this.tooltip && this.tooltipText()) {
          const text =
            this.tooltipText() || this.el.nativeElement.textContent!.trim();
          const translatedText = this.canBeTranslated()
            ? this.i18nService.translate(text)
            : text;
          // Use model's .set() method for bidirectional binding
          this.tooltip.tooltipText.set(translatedText);
        }
      },
      {
        root: this.el.nativeElement.parentElement,
        threshold: [1.0],
      },
    );

    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }
}
